import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assests/logo.png';
import { useAuth } from '../auth/AuthContext';
import { api } from '../services/api';
import { CART_UPDATED_EVENT, readCartItem } from '../utils/checkoutCart';

const injectNavStyles = () => {
    if (document.getElementById('nav-styles-v4')) return;
    const s = document.createElement('style');
    s.id = 'nav-styles-v4';
    s.textContent = `
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

        header * { box-sizing: border-box; }
        header { font-family: 'DM Sans', sans-serif; position: sticky; top: 0; z-index: 1000; }

        /* ── One-time entry animations ── */
        @keyframes nav-drop  { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes nav-fade  { from{opacity:0} to{opacity:1} }
        @keyframes nav-slide { from{opacity:0;transform:translateX(-14px)} to{opacity:1;transform:translateX(0)} }

        /* ── Top Bar ── */
        .top-bar {
            background: #1a6db5;
            color: rgba(255,255,255,.9);
            font-size: 12.5px;
            font-weight: 500;
            animation: nav-drop .4s cubic-bezier(.34,1.45,.64,1) both;
        }
        .top-bar__inner {
            max-width: 1580px; margin: 0 auto; padding: 7px 20px;
            display: flex; align-items: center; justify-content: space-between;
        }
        .top-bar__left, .top-bar__right { display: flex; align-items: center; gap: 16px; }
        .top-bar__item { display: flex; align-items: center; gap: 6px; color: rgba(255,255,255,.9); text-decoration: none; transition: color .18s ease; }
        .top-bar__item:hover { color: #fff; }
        .top-bar__item svg { width: 13px; height: 13px; flex-shrink: 0; }

        /* ── Main Header ── */
        .main-header {
            background: #fff;
            border-bottom: 1px solid #e8eef7;
            transition: box-shadow .25s ease;
            animation: nav-fade .4s ease both .08s;
        }
        .main-header.scrolled { box-shadow: 0 4px 20px rgba(0,0,0,.09); }
        .main-header__inner {
            max-width: 1580px; margin: 0 auto; padding: 10px 20px;
            display: flex; align-items: center; gap: 16px;
        }

        /* Logo */
        .header-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; flex-shrink: 0; }
        .header-logo__brand img { height: 46px; width: auto; display: block; object-fit: contain; }

        /* Search */
        .header-search { flex: 1; min-width: 0; position: relative; }
        .header-search__form {
            display: flex; align-items: center; background: #f4f7fc;
            border: 1.5px solid #dce6f5; border-radius: 8px; overflow: hidden;
            transition: border-color .2s ease, box-shadow .2s ease;
        }
        .header-search__form:focus-within { border-color: #3991D5; box-shadow: 0 0 0 3px rgba(57,145,213,.12); }
        .header-search__input { flex: 1; border: none; background: transparent; padding: 9px 14px; font-size: 13.5px; outline: none; color: #1a202c; font-family: 'DM Sans', sans-serif; }
        .header-search__input::placeholder { color: #94a3b8; }
        .header-search__btn { background: #3991D5; border: none; cursor: pointer; padding: 0 16px; height: 40px; display: flex; align-items: center; flex-shrink: 0; transition: background .18s ease; }
        .header-search__btn:hover { background: #1a6db5; }
        .header-search__btn svg { width: 16px; height: 16px; stroke: #fff; }
        .header-search__dropdown {
            position: absolute; left: 0; right: 0; top: calc(100% + 10px); z-index: 1300;
            overflow: hidden; border: 1px solid #e8eef7; border-radius: 16px; background: #fff;
            box-shadow: 0 18px 45px rgba(15,23,42,.16);
        }
        .header-search__status {
            display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #edf3fa;
            padding: 11px 14px; color: #64748b; font-size: 12px; font-weight: 800;
        }
        .header-search__spinner {
            width: 14px; height: 14px; border: 2px solid #dbeafe; border-top-color: #3991D5;
            border-radius: 999px; animation: nav-spin .7s linear infinite;
        }
        @keyframes nav-spin { to { transform: rotate(360deg); } }
        .header-search__result {
            display: flex; width: 100%; align-items: center; gap: 12px; border: 0; border-bottom: 1px solid #f1f5f9;
            background: #fff; padding: 12px 14px; color: #334155; cursor: pointer; font-family: 'DM Sans', sans-serif;
            text-align: left; transition: background .15s ease, color .15s ease;
        }
        .header-search__result:hover { background: #f0f8ff; color: #1278b5; }
        .header-search__result-icon {
            display: flex; height: 36px; width: 36px; flex-shrink: 0; align-items: center; justify-content: center;
            border-radius: 12px; background: #e8f6ff; color: #1278b5; font-size: 11px; font-weight: 900;
            text-transform: uppercase;
        }
        .header-search__result-copy { min-width: 0; flex: 1; }
        .header-search__result-title { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 900; color: #0f172a; }
        .header-search__result-meta { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-top: 2px; font-size: 11px; font-weight: 700; color: #94a3b8; }
        .header-search__result-type { border-radius: 999px; background: #f1f5f9; padding: 4px 8px; color: #64748b; font-size: 10px; font-weight: 900; text-transform: uppercase; }
        .header-search__empty { padding: 14px; color: #64748b; font-size: 12px; font-weight: 800; }

        /* Actions */
        .header-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; margin-left: auto; }
        .header-divider { width: 1px; height: 28px; background: #e8eef7; margin: 0 4px; }
        .header-action-btn { display: flex; align-items: center; gap: 0 8px; padding: 7px 10px; border-radius: 8px; text-decoration: none; color: #374151; position: relative; transition: background .18s ease, color .18s ease; }
        .header-action-btn:hover { background: #f0f6ff; color: #3991D5; }
        .header-action-btn svg { width: 20px; height: 20px; flex-shrink: 0; }
        .header-action-btn--account { border: 1px solid #dce6f5; background: #fff; }
        .header-action-btn--primary { background: #3991D5; color: #fff;}
        .header-action-btn--primary svg { color: #fff;}
        .header-action-btn--primary:hover { background: #1a6db5; color: #fff; }
        .header-action-btn--danger { border: 0; background: transparent; cursor: pointer; font-family: inherit; }
        .header-action-btn--danger:hover { color: #dc2626; background: #fff1f2; }
        .bb-account-popover { position: relative; }
        .bb-account-trigger {
            display: flex; align-items: center; gap: 7px; height: 38px; max-width: 150px;
            border: 1px solid #dce6f5; border-radius: 999px; background: #fff; padding: 4px 8px 4px 4px;
            color: #1f2937; cursor: pointer; font-family: inherit; transition: border-color .18s ease, box-shadow .18s ease, color .18s ease;
        }
        .bb-account-trigger:hover { border-color: #3991D5; box-shadow: 0 6px 18px rgba(57,145,213,.12); color: #1a6db5; }
        .bb-account-avatar {
            width: 28px; height: 28px; border-radius: 999px; background: #3991D5; color: #fff;
            display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 900; flex-shrink: 0;
        }
        .bb-account-name { min-width: 0; max-width: 82px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; font-weight: 900; }
        .bb-account-trigger svg { width: 12px; height: 12px; flex-shrink: 0; }
        .bb-account-menu {
            position: absolute; right: 0; top: calc(100% + 10px); z-index: 1200;
            width: 235px; overflow: hidden; border: 1px solid #e8eef7; border-radius: 14px;
            background: #fff; box-shadow: 0 18px 45px rgba(15,23,42,.14);
        }
        .bb-account-head { padding: 13px; background: #f8fbff; border-bottom: 1px solid #e8eef7; }
        .bb-account-head-name { margin: 0; font-size: 13px; font-weight: 900; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .bb-account-head-email { margin: 3px 0 0; font-size: 11px; font-weight: 600; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .bb-account-role { display: inline-flex; margin-top: 8px; border-radius: 999px; background: #e0f4ff; color: #1a6db5; padding: 3px 8px; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: .05em; }
        .bb-account-menu-link,
        .bb-account-menu-button {
            display: flex; width: 100%; align-items: center; gap: 9px; padding: 11px 13px;
            border: 0; background: #fff; color: #334155; text-decoration: none; font-size: 12px; font-weight: 850;
            font-family: inherit; cursor: pointer; text-align: left;
        }
        .bb-account-menu-link svg,
        .bb-account-menu-button svg { width: 16px; height: 16px; }
        .bb-account-menu-link:hover,
        .bb-account-menu-button:hover { background: #f0f6ff; color: #3991D5; }
        .bb-account-menu-button.bb-danger:hover { background: #fff1f2; color: #dc2626; }
        .header-user-chip {
            display: flex; flex-direction: column; line-height: 1.15; min-width: 0;
        }
        .header-user-chip__name { max-width: 130px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; font-weight: 800; color: inherit; }
        .header-user-chip__role { font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #64748b; }
        .header-action-btn--primary .header-user-chip__role { color: rgba(255,255,255,.8); }

        /* Call section */
        .calls { display: flex; align-items: center; gap: 8px; }
        .header-action-icon svg { width: 20px; height: 20px; }
        .header-action-text { display: flex; flex-direction: column; line-height: 1.25; }
        .header-action-text .label { font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
        .header-action-text .phone { font-size: 13.5px; color: #1a202c; font-weight: 700; }

        /* Cart */
        .header-action-btn--cart { background: #3991D5; border: 1.5px solid #3991D5; border-radius: 8px; padding: 7px 14px; }
        .header-action-btn--cart:hover { background: #2c74aa; color: #fff; border-color: #226292; }
        .header-action-btn--cart:hover .cart-info__count,
        .header-action-btn--cart:hover .cart-info__total { color: rgba(255,255,255,.9); }
        .cart-info { display: flex; flex-direction: column; line-height: 1.2; }
        .cart-info__count { font-size: 10px; color: #94a3b8; font-weight: 600; }
        .cart-info__total { font-size: 14px; color: #1a202c; font-weight: 700; }

        /* Badge */
        .header-action-btn__badge { position: absolute; top: 2px; right: 2px; background: #ef4444; color: #fff; font-size: 10px; font-weight: 700; min-width: 17px; height: 17px; border-radius: 9px; display: flex; align-items: center; justify-content: center; padding: 0 4px; }

        /* Mobile toggle */
        .mobile-menu-btn { display: none; background: none; border: 1.5px solid #e0e8f4; border-radius: 8px; width: 40px; height: 40px; align-items: center; justify-content: center; cursor: pointer; color: #374151; flex-shrink: 0; transition: background .18s ease; }
        .mobile-menu-btn:hover { background: #f0f6ff; }
        .mobile-menu-btn svg { width: 20px; height: 20px; }

        /* ── Nav Bar ── */
        .nav-bar {
            background: #2196f3;
            border-bottom: 1px solid rgba(0,0,0,.06);
            animation: nav-drop .42s cubic-bezier(.34,1.45,.64,1) both .15s;
        }
        .nav-bar__inner {
            max-width: 1580px; margin: 0 auto; padding: 0 20px;
            display: flex; align-items: center; justify-content: space-between;
        }
        .nav-bar__list { display: flex; align-items: center; list-style: none; margin: 0; padding: 0; flex-wrap: nowrap; }
        .nav-bar__item { position: relative; }

        /* Staggered link animations */
        .nav-bar__item:nth-child(1)  .nav-bar__link { animation: nav-slide .3s ease both .20s; }
        .nav-bar__item:nth-child(2)  .nav-bar__link { animation: nav-slide .3s ease both .24s; }
        .nav-bar__item:nth-child(3)  .nav-bar__link { animation: nav-slide .3s ease both .28s; }
        .nav-bar__item:nth-child(4)  .nav-bar__link { animation: nav-slide .3s ease both .32s; }
        .nav-bar__item:nth-child(5)  .nav-bar__link { animation: nav-slide .3s ease both .36s; }
        .nav-bar__item:nth-child(6)  .nav-bar__link { animation: nav-slide .3s ease both .40s; }
        .nav-bar__item:nth-child(7)  .nav-bar__link { animation: nav-slide .3s ease both .44s; }
        .nav-bar__item:nth-child(8)  .nav-bar__link { animation: nav-slide .3s ease both .48s; }
        .nav-bar__item:nth-child(9)  .nav-bar__link { animation: nav-slide .3s ease both .52s; }
        .nav-bar__item:nth-child(10) .nav-bar__link { animation: nav-slide .3s ease both .56s; }
        .nav-bar__item:nth-child(11) .nav-bar__link { animation: nav-slide .3s ease both .60s; }

        .nav-bar__link {
            display: flex; align-items: center; gap: 3px; padding: 12px 13px;
            color: rgba(255,255,255,.92); font-size: 13px; font-weight: 600;
            text-decoration: none; white-space: nowrap; font-family: 'DM Sans', sans-serif;
            border-bottom: 2px solid transparent;
            transition: color .18s ease, background .18s ease, border-color .18s ease;
            /* Keep opacity:0 until animation fills it */
            opacity: 0;
        }
        .nav-bar__link svg { width: 12px; height: 12px; opacity: .75; flex-shrink: 0; }
        .nav-bar__link:hover { color: #fff; background: rgba(0,0,0,.1); border-bottom-color: rgba(255,255,255,.6); }

        /* Active/current page */
        .nav-bar__link.active { border-bottom-color: #fff; color: #fff; }

        /* Dropdown */
        .nav-dropdown {
            position: absolute; top: 100%; left: 0; z-index: 500;
            background: #fff; border: 1px solid #e0e8f4; border-radius: 12px;
            min-width: 240px; box-shadow: 0 12px 36px rgba(0,0,0,.12);
            padding: 6px; opacity: 0; visibility: hidden; pointer-events: none;
            transform: translateY(8px);
            transition: opacity .2s ease, transform .2s ease, visibility .2s ease;
        }
        .nav-bar__item:hover .nav-dropdown { opacity: 1; visibility: visible; pointer-events: all; transform: translateY(0); }
        .nav-dropdown__link { display: block; padding: 9px 14px; color: #374151; font-size: 13px; font-weight: 500; text-decoration: none; border-radius: 8px; font-family: 'DM Sans', sans-serif; transition: background .15s ease, color .15s ease; }
        .nav-dropdown__link:hover { background: #f0f6ff; color: #3991D5; }
        .nav-dropdown--shop { width: min(880px, calc(100vw - 40px)); padding: 12px; }
        .nav-dropdown__panel { display: grid; grid-template-columns: 190px minmax(0,1fr); gap: 12px; }
        .nav-dropdown__utility { border-right: 1px solid #edf3fa; padding-right: 10px; }
        .nav-dropdown__eyebrow { display: block; margin: 4px 8px 8px; color: #3991D5; font-size: 10px; font-weight: 900; letter-spacing: .1em; text-transform: uppercase; }
        .nav-dropdown__categories { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; max-height: 430px; overflow-y: auto; padding-right: 2px; }
        .nav-dropdown__category { border: 1px solid #edf3fa; border-radius: 10px; padding: 9px; background: #fff; }
        .nav-dropdown__category:hover { border-color: #cfe2f7; background: #fbfdff; }
        .nav-dropdown__category-main { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; color: #172033; text-decoration: none; }
        .nav-dropdown__category-title { min-width: 0; font-size: 13px; font-weight: 900; line-height: 1.25; }
        .nav-dropdown__category-count { flex-shrink: 0; border-radius: 999px; background: #eaf4fc; color: #1a6db5; padding: 3px 7px; font-size: 10px; font-weight: 900; }
        .nav-dropdown__subcategories { display: grid; gap: 2px; margin-top: 7px; }
        .nav-dropdown__subcategory { display: block; overflow: hidden; border-radius: 7px; color: #64748b; font-size: 12px; font-weight: 700; line-height: 1.25; padding: 5px 6px; text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }
        .nav-dropdown__subcategory:hover { background: #f0f6ff; color: #3991D5; }
        .nav-dropdown__more { display: inline-flex; margin-top: 5px; color: #3991D5; font-size: 11px; font-weight: 900; text-decoration: none; }

        /* Right links */
        .nav-bar__right { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
        .nav-bar__right-link { display: flex; align-items: center; gap: 6px; padding: 8px 12px; color: rgba(255,255,255,.85); font-size: 12.5px; font-weight: 600; text-decoration: none; border-radius: 8px; white-space: nowrap; font-family: 'DM Sans', sans-serif; transition: background .18s ease, color .18s ease; }
        .nav-bar__right-link svg { width: 15px; height: 15px; flex-shrink: 0; }
        .nav-bar__right-link:hover { background: rgba(0,0,0,.1); color: #fff; }
        .nav-bar__right-button { border: 0; background: transparent; cursor: pointer; font-family: 'DM Sans', sans-serif; }

        /* ══ RESPONSIVE ══ */
        @media (max-width: 1100px) {
            .header-action-text { display: none; }
            .calls { gap: 0; }
        }
        @media (max-width: 1536px) {
            .nav-bar__item--category:nth-child(7) { display: none; }
            .nav-bar__inner { justify-content: space-between; }
            .nav-bar__link { padding: 12px 12px; }
        }
        @media (max-width: 1440px) {
            .nav-bar__item--category:nth-child(6) { display: none; }
        }
        @media (max-width: 1320px) {
            .nav-bar__item--category:nth-child(5) { display: none; }
            .nav-bar__link { padding: 12px 11px; font-size: 12.5px; }
        }
        @media (max-width: 1200px) {
            .nav-bar__item--category:nth-child(4) { display: none; }
        }
        @media (max-width: 1100px) {
            .nav-bar__item--category:nth-child(3) { display: none; }
        }
        @media (max-width: 991px) {
            .nav-bar__right { display: none; }
            .nav-bar__list { overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
            .nav-bar__list::-webkit-scrollbar { display: none; }
            .nav-bar__link { padding: 12px 10px; font-size: 12px; }
        }
        @media (max-width: 767px) {
            .top-bar__right { display: none; }
            .top-bar__inner { padding: 6px 14px; }
            .main-header__inner { padding: 10px 14px; gap: 10px; }
            .header-search { display: none; }
            .mobile-menu-btn { display: flex; }
            .header-divider { display: none; }
            .cart-info { display: none; }
            .header-action-btn--cart { padding: 7px 10px; }
            .header-action-icon { display: none; }

            /* Mobile nav: full drawer */
            .nav-bar {
                position: fixed; top: 0; left: -100%; right: auto;
                width: 82%; max-width: 320px; height: 100vh; z-index: 1100;
                overflow-y: auto; background: #1a6db5;
                transition: left .3s cubic-bezier(.4,0,.2,1);
                animation: none;
                padding-top: 60px;
            }
            .nav-bar.open { left: 0; }
            .nav-bar__inner { flex-direction: column; align-items: flex-start; padding: 0; gap: 0; }
            .nav-bar__list { flex-direction: column; width: 100%; overflow: visible; }
            .nav-bar__item { width: 100%; }
            .nav-bar__link { padding: 14px 20px; font-size: 14px; border-bottom: 1px solid rgba(255,255,255,.08); border-left: none; opacity: 1 !important; animation: none !important; }
            .nav-dropdown { position: static; background: rgba(0,0,0,.15); box-shadow: none; border: none; border-radius: 0; padding: 0; opacity: 1; visibility: visible; pointer-events: all; transform: none; display: none; width: 100%; min-width: 0; }
            .nav-dropdown--shop { width: 100%; padding: 0; }
            .nav-dropdown__panel { display: block; }
            .nav-dropdown__utility { border-right: 0; padding: 6px 12px; }
            .nav-dropdown__categories { display: block; max-height: none; padding: 0 12px 12px; overflow: visible; }
            .nav-dropdown__category { margin-top: 6px; border-color: rgba(255,255,255,.1); background: rgba(255,255,255,.06); }
            .nav-dropdown__category-main { color: #fff; }
            .nav-dropdown__subcategory { color: rgba(255,255,255,.82); }
            .nav-dropdown__subcategory:hover { background: rgba(255,255,255,.08); color: #fff; }
            .nav-dropdown__link { color: rgba(255,255,255,.9); }
            .nav-dropdown__link:hover { background: rgba(255,255,255,.08); color: #fff; }
            .nav-bar__item:hover .nav-dropdown { display: none; }
            .nav-bar.open .nav-dropdown { display: block; }
            .nav-bar__right { display: flex; flex-direction: column; align-items: flex-start; padding: 16px 20px; border-top: 1px solid rgba(255,255,255,.1); width: 100%; gap: 8px; }
            .nav-bar__right-link { font-size: 14px; padding: 10px 0; }

            /* Overlay */
            .nav-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1099; }
            .nav-overlay.open { display: block; }

            /* Close btn inside drawer */
            .nav-close-btn { position: absolute; top: 14px; right: 14px; background: rgba(255,255,255,.12); border: none; border-radius: 8px; width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #fff; z-index: 10; }
            .nav-close-btn svg { width: 18px; height: 18px; }
        }
        @media (max-width: 400px) {
            .header-logo__brand img { height: 38px; }
        }
    `;
    document.head.appendChild(s);
};

/* Icons */
const GuestCheckoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* User */}
    <circle cx="12" cy="7" r="4"/>
    <path d="M5.5 21a6.5 6.5 0 0 1 13 0"/>

    {/* Lightning bolt (quick/guest) */}
    <polyline points="13 10 11 14 14 14 12 18" />
  </svg>
);

const PhoneIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;
const MailIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><polyline points="3,7 12,13 21,7"/></svg>;
const SearchIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const UserIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const HeartIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>;
const CartIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>;
const ChevronDown = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
const MenuIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
const CloseIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const LogoutIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
const DashboardIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>;
const TrackIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10V8a2 2 0 0 0-2-2h-3"/><path d="M3 10V8a2 2 0 0 1 2-2h3"/><path d="M21 14v2a2 2 0 0 1-2 2h-3"/><path d="M3 14v2a2 2 0 0 0 2 2h3"/><circle cx="12" cy="12" r="3"/><path d="M12 3v3"/><path d="M12 18v3"/></svg>;
const CustomOrderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="6" x2="20" y2="6"/>
    <circle cx="10" cy="6" r="2"/>
    
    <line x1="4" y1="12" x2="20" y2="12"/>
    <circle cx="14" cy="12" r="2"/>
    
    <line x1="4" y1="18" x2="20" y2="18"/>
    <circle cx="8" cy="18" r="2"/>
  </svg>
);

const NAV_LINKS = [
    { label:'Home', href:'/' },
    { label:'Contact', href:'/contact' },
    // { label:'Support', href:'/support' },
    { label:'Part Book PDF', href:'/part-book' },
];

const sortCategories = (items = []) => [...items].sort((a, b) => {
    const aOrder = Number(a?.menuOrder ?? a?.sortOrder ?? 0);
    const bOrder = Number(b?.menuOrder ?? b?.sortOrder ?? 0);
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
});

const categoryHref = (category) => `/all-products?categorySlug=${encodeURIComponent(category.slug)}`;
const subcategoryHref = (category, subcategory) => `${categoryHref(category)}&subcategorySlug=${encodeURIComponent(subcategory.slug)}`;
const productHref = (product) => `/products/${encodeURIComponent(product?.slug || product?.sku || product?._id || product?.id)}`;

const flattenCategoryTree = (items = [], parentNames = []) => items.flatMap((category) => {
    const children = category.subcategories || category.children || [];
    const pathLabel = [...parentNames, category.name].filter(Boolean).join(' / ');
    return [
        { ...category, pathLabel },
        ...flattenCategoryTree(children, [...parentNames, category.name]),
    ];
});

const HEADER_CATEGORY_LINKS = [
    {
        label: 'Pegasus Parts',
        fallbackHref: '/all-products?categorySlug=pegasus-parts',
        matches: ['pegasus-parts', 'pegasus-machine-and-parts', 'pegasus parts'],
    },
    {
        label: 'Juki Machine Parts',
        fallbackHref: '/all-products?categorySlug=juki-parts',
        matches: ['juki-parts', 'juki-machine-and-parts', 'juki parts', 'juki machine parts'],
    },
    {
        label: 'Ever Peak Foots',
        fallbackHref: '/all-products?categorySlug=ever-peak-foots',
        matches: ['ever-peak-foots', 'ever peak foots', 'ever peak'],
    },
    {
        label: 'Cutting Machine Parts',
        fallbackHref: '/all-products?categorySlug=cutting-machine-parts',
        matches: ['cutting-machine-parts', 'cutting machine parts', 'cutting machine'],
    },
    {
        label: 'Iron Parts',
        fallbackHref: '/all-products?categorySlug=iron-parts',
        matches: ['iron-and-boiler-parts', 'iron-parts', 'iron parts', 'iron - boiler parts', 'iron'],
    },
];

const findHeaderCategory = (categories, matches) => {
    const normalizedMatches = matches.map((item) => item.toLowerCase());
    return categories.find((category) => {
        const slug = String(category?.slug || '').toLowerCase();
        const name = String(category?.name || '').toLowerCase();
        return normalizedMatches.some((match) => slug === match || name === match || slug.includes(match) || name.includes(match));
    });
};

const Navbar = ({ wishlistCount = 0 }) => {
    useEffect(() => { injectNavStyles(); }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, isAuthenticated, loading, logout, user } = useAuth();
    const [scrolled,  setScrolled]  = useState(false);
    const [menuOpen,  setMenuOpen]  = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [searchVal, setSearchVal] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cartItem, setCartItem] = useState(() => readCartItem());

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    useEffect(() => {
        let mounted = true;
        api.get('/categories')
            .then((data) => {
                if (mounted) setCategories(data.categories || []);
            })
            .catch(() => {
                if (mounted) setCategories([]);
            });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const syncCart = () => setCartItem(readCartItem());
        window.addEventListener(CART_UPDATED_EVENT, syncCart);
        window.addEventListener('storage', syncCart);
        return () => {
            window.removeEventListener(CART_UPDATED_EVENT, syncCart);
            window.removeEventListener('storage', syncCart);
        };
    }, []);

    useEffect(() => {
        const term = searchVal.trim();

        if (term.length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            return undefined;
        }

        let cancelled = false;
        const timer = window.setTimeout(async () => {
            setSearchLoading(true);

            try {
                const [productResponse, categoryResponse] = await Promise.all([
                    api.get(`/products/public?search=${encodeURIComponent(term)}&limit=6`).catch(() => null),
                    categories.length ? Promise.resolve({ categories }) : api.get('/categories').catch(() => null),
                ]);

                if (cancelled) return;

                const products = Array.isArray(productResponse?.products)
                    ? productResponse.products.slice(0, 5).map((product) => ({
                        key: `product:${product._id || product.id || product.sku}`,
                        href: productHref(product),
                        label: product.name || product.sku || 'Untitled product',
                        description: [product.sku, product.brandLine, product.category].filter(Boolean).join(' / ') || 'Product',
                        type: 'Product',
                        iconText: 'P',
                    }))
                    : [];

                const categoryTerm = term.toLowerCase();
                const categoryMatches = flattenCategoryTree(categoryResponse?.categories || [])
                    .filter((category) =>
                        [category.name, category.slug, category.pathLabel, category.wcId]
                            .join(' ')
                            .toLowerCase()
                            .includes(categoryTerm)
                    )
                    .slice(0, 5)
                    .map((category) => ({
                        key: `category:${category._id || category.wcId || category.slug || category.name}`,
                        href: category.slug ? categoryHref(category) : '/categories',
                        label: category.name || 'Untitled category',
                        description: category.pathLabel || category.slug || 'Category',
                        type: 'Category',
                        iconText: 'C',
                    }));

                setSearchResults([...products, ...categoryMatches].slice(0, 8));
            } finally {
                if (!cancelled) setSearchLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [categories, searchVal]);

    const handleSearch = (e) => {
        e.preventDefault();
        const term = searchVal.trim();
        if (!term) return;
        const firstResult = searchResults[0];
        navigate(firstResult?.href || `/all-products?search=${encodeURIComponent(term)}`);
        setSearchVal('');
        setSearchOpen(false);
        setMenuOpen(false);
    };

    const handleNavClick = (href) => {
        setMenuOpen(false);
    };

    const isNavLinkActive = (href) => {
        const [pathname, query = ""] = href.split("?");
        if (href === "/") return location.pathname === "/";
        if (query) return location.pathname === pathname && location.search === `?${query}`;
        return location.pathname === pathname;
    };

    const accountHref = isAdmin ? '/dashboard' : '/my-account';
    const accountLabel = isAdmin ? 'Admin Dashboard' : 'My Account';
    const displayName = user?.name || user?.email || 'Account';
    const cartCount = Number(cartItem?.quantity || 0);
    const cartTotal = Number(cartItem?.price || 0) * cartCount;
    const initials = displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase() || 'AC';
    const handleLogout = () => {
        logout();
        setMenuOpen(false);
        setAccountOpen(false);
        navigate('/account');
    };

    const sortedCategories = useMemo(() => sortCategories(categories), [categories]);
    const shopDropdown = useMemo(() => ({
        type: 'shop',
        utility: [
        { label: 'All Products', href: '/all-products' },
        { label: 'All Categories', href: '/categories' },
        { label: 'Featured products', href: '/all-products?section=featured' },
        { label: 'Popular products', href: '/all-products?section=popular' },
        { label: 'Most viewed products', href: '/all-products?section=mostViewed' },
        { label: 'Top selling', href: '/all-products?section=topSelling' },
        ],
        categories: sortedCategories.map((category) => ({
            ...category,
            href: categoryHref(category),
            subcategories: sortCategories(category.subcategories || []).map((subcategory) => ({
                ...subcategory,
                href: subcategoryHref(category, subcategory),
            })),
        })),
    }), [sortedCategories]);
    const headerCategoryLinks = useMemo(() => HEADER_CATEGORY_LINKS.map((item) => {
        const category = findHeaderCategory(sortedCategories, item.matches);
        if (!category) {
            return { label: item.label, href: item.fallbackHref };
        }

        const subcategories = sortCategories(category.subcategories || []);
        return {
            label: item.label,
            href: categoryHref(category),
            dropdown: [
                { label: `All ${item.label}`, href: categoryHref(category) },
                ...subcategories.map((subcategory) => ({
                    label: subcategory.name,
                    href: subcategoryHref(category, subcategory),
                })),
            ],
        };
    }), [sortedCategories]);
    const navLinks = useMemo(() => [
        NAV_LINKS[0],
        { label: 'Shop', href: '/all-products', dropdown: shopDropdown },
        ...headerCategoryLinks.map((link) => ({ ...link, variant: 'category' })),
        ...NAV_LINKS.slice(1),
    ], [headerCategoryLinks, shopDropdown]);

    const renderDropdown = (dropdown) => {
        if (dropdown?.type === 'shop') {
            return (
                <div className="nav-dropdown__panel">
                    <div className="nav-dropdown__utility">
                        <span className="nav-dropdown__eyebrow">Shop</span>
                        {dropdown.utility.map((item) => (
                            <Link key={item.href} to={item.href} className="nav-dropdown__link" onClick={() => handleNavClick(item.href)}>
                                {item.label}
                            </Link>
                        ))}
                    </div>
                    <div className="nav-dropdown__categories">
                        {dropdown.categories.map((category) => (
                            <div className="nav-dropdown__category" key={category.slug || category.name}>
                                <Link to={category.href} className="nav-dropdown__category-main" onClick={() => handleNavClick(category.href)}>
                                    <span className="nav-dropdown__category-title">{category.name}</span>
                                    <span className="nav-dropdown__category-count">{Number(category.count || 0)}</span>
                                </Link>
                                {!!category.subcategories?.length && (
                                    <div className="nav-dropdown__subcategories">
                                        {category.subcategories.slice(0, 5).map((subcategory) => (
                                            <Link
                                                key={subcategory.slug || subcategory.name}
                                                to={subcategory.href}
                                                className="nav-dropdown__subcategory"
                                                onClick={() => handleNavClick(subcategory.href)}
                                            >
                                                {subcategory.name}
                                            </Link>
                                        ))}
                                        {category.subcategories.length > 5 && (
                                            <Link to={category.href} className="nav-dropdown__more" onClick={() => handleNavClick(category.href)}>
                                                +{category.subcategories.length - 5} more
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return dropdown.map(sub => (
            <Link key={sub.label} to={sub.href} className="nav-dropdown__link" onClick={() => handleNavClick(sub.href)}>{sub.label}</Link>
        ));
    };

    return (
        <header>
            {/* Top Bar */}
            <div className="top-bar">
                <div className="top-bar__inner">
                    <div className="top-bar__left">
                        <a href="tel:2137002967" className="top-bar__item"><PhoneIcon /> 213-700-2967</a>
                    </div>
                    <div className="top-bar__right">
                        <span className="top-bar__item"><MailIcon /> bobbinusa@gmail.com</span>
                    </div>
                </div>
            </div>

            {/* Main Header */}
            <div className={`main-header${scrolled ? ' scrolled' : ''}`}>
                <div className="main-header__inner">
                    <Link to="/" className="header-logo">
                        <div className="header-logo__text">
                            <span className="header-logo__brand"><img src={Logo} alt="Bobbin USA" /></span>
                        </div>
                    </Link>

                    <div className="header-search">
                        <form className="header-search__form" onSubmit={handleSearch}>
                            <input
                                className="header-search__input"
                                type="text"
                                placeholder="Search product, SKU, category..."
                                value={searchVal}
                                onChange={e => {
                                    setSearchVal(e.target.value);
                                    setSearchOpen(true);
                                }}
                                onFocus={() => setSearchOpen(true)}
                            />
                            <button className="header-search__btn" type="submit" aria-label="Search"><SearchIcon /></button>
                        </form>
                        {searchOpen && searchVal.trim() && (
                            <div className="header-search__dropdown">
                                {searchLoading && (
                                    <div className="header-search__status">
                                        <span className="header-search__spinner" />
                                        Searching products and categories
                                    </div>
                                )}
                                {searchResults.length ? (
                                    searchResults.map((result) => (
                                        <button
                                            className="header-search__result"
                                            key={result.key}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                navigate(result.href);
                                                setSearchVal('');
                                                setSearchOpen(false);
                                                setMenuOpen(false);
                                            }}
                                            type="button"
                                        >
                                            <span className="header-search__result-icon">{result.iconText}</span>
                                            <span className="header-search__result-copy">
                                                <span className="header-search__result-title">{result.label}</span>
                                                <span className="header-search__result-meta">{result.description}</span>
                                            </span>
                                            <span className="header-search__result-type">{result.type}</span>
                                        </button>
                                    ))
                                ) : (
                                    !searchLoading && (
                                        <div className="header-search__empty">
                                            No products or categories found. Press enter to search all products.
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>

                    <div className="header-actions">
                        {isAuthenticated && (
                            <Link to="/my-account" className="header-action-btn" aria-label="Wishlist">
                                <HeartIcon />
                                {wishlistCount > 0 && <span className="header-action-btn__badge">{wishlistCount}</span>}
                            </Link>
                        )}
                        {!loading && (
                            isAuthenticated ? (
                                <div className="bb-account-popover">
                                    <button
                                        className="bb-account-trigger"
                                        onClick={() => setAccountOpen(open => !open)}
                                        type="button"
                                        aria-label="Account menu"
                                    >
                                        <span className="bb-account-avatar">{initials}</span>
                                        <span className="bb-account-name">{displayName}</span>
                                        <ChevronDown />
                                    </button>
                                    {accountOpen && (
                                        <div className="bb-account-menu">
                                            <div className="bb-account-head">
                                                <p className="bb-account-head-name">{displayName}</p>
                                                <p className="bb-account-head-email">{user?.email}</p>
                                                <span className="bb-account-role">{isAdmin ? 'Admin' : 'Customer'}</span>
                                            </div>
                                            <Link className="bb-account-menu-link" to={accountHref} onClick={() => setAccountOpen(false)}>
                                                {isAdmin ? <DashboardIcon /> : <UserIcon />}
                                                {accountLabel}
                                            </Link>
                                            {!isAdmin && (
                                                <Link className="bb-account-menu-link" to="/my-account" onClick={() => setAccountOpen(false)}>
                                                    <HeartIcon />
                                                    Wishlist & Orders
                                                </Link>
                                            )}
                                            <Link className="bb-account-menu-link" to="/track-order" onClick={() => setAccountOpen(false)}>
                                                <TrackIcon />
                                                Track Order
                                            </Link>
                                            <button className="bb-account-menu-button bb-danger" onClick={handleLogout} type="button">
                                                <LogoutIcon />
                                                Sign out
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link to="/account" className="header-action-btn header-action-btn--primary">
                                    <UserIcon />
                                    Login
                                </Link>
                            )
                        )}
                        <div className="header-divider" />
                        <a href="tel:2137002967" className="header-action-btn" aria-label="Call Us">
                            <div className="calls">
                                <div className="header-action-icon"><PhoneIcon /></div>
                                <div className="header-action-text">
                                    <span className="label">Call Us At</span>
                                    <span className="phone">213-700-2967</span>
                                </div>
                            </div>
                        </a>
                        <div className="header-divider" />
                        <Link to="/cart" className="header-action-btn header-action-btn--cart" aria-label="Cart">
                            <CartIcon />
                            <div className="cart-info">
                                <span className="cart-info__count">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
                                <span className="cart-info__total">${cartTotal.toFixed(2)}</span>
                            </div>
                        </Link>
                        <button className="mobile-menu-btn" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
                            <MenuIcon />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile overlay */}
            <div className={`nav-overlay${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(false)} />

            {/* Nav Bar */}
            <nav className={`nav-bar${menuOpen ? ' open' : ''}`}>
                {/* Mobile close button */}
                <button className="nav-close-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu" style={{ display: menuOpen ? 'flex' : 'none' }}>
                    <CloseIcon />
                </button>

                <div className="nav-bar__inner">
                    <ul className="nav-bar__list">
                        {navLinks.map(({ label, href, dropdown, variant }) => (
                            <li key={label} className={`nav-bar__item${variant === 'category' ? ' nav-bar__item--category' : ''}`}>
                                <Link
                                    to={href}
                                    className={`nav-bar__link${isNavLinkActive(href) ? " active" : ""}`}
                                    onClick={() => handleNavClick(href)}
                                >
                                    {label}{dropdown && <ChevronDown />}
                                </Link>
                                {dropdown && (
                                    <div className={`nav-dropdown${dropdown.type === 'shop' ? ' nav-dropdown--shop' : ''}`}>
                                        {renderDropdown(dropdown)}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="nav-bar__right">
                        <Link to="/track-order" className="nav-bar__right-link" onClick={() => setMenuOpen(false)}><TrackIcon />Track Order</Link>
                        {isAdmin && (
                            <Link to="/dashboard/custom-order" className="nav-bar__right-link" onClick={() => setMenuOpen(false)}><CustomOrderIcon />Custom order</Link>
                        )}
                        {!loading && (
                            isAuthenticated ? (
                                <>
                                    <Link to={accountHref} className="nav-bar__right-link" onClick={() => setMenuOpen(false)}>
                                        {isAdmin ? <DashboardIcon /> : <UserIcon />}
                                        {accountLabel}
                                    </Link>
                                    <button className="nav-bar__right-link nav-bar__right-button" onClick={handleLogout} type="button">
                                        <LogoutIcon />
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link to="/account" className="nav-bar__right-link" onClick={() => setMenuOpen(false)}><UserIcon />Login / Register</Link>
                                    <Link to="/checkout" className="nav-bar__right-link" onClick={() => setMenuOpen(false)}><GuestCheckoutIcon />Guest Checkout</Link>
                                </>
                            )
                        )}
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Navbar;
